import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import CameraFileInput from '../components/CameraFileInput';
import { openProtectedFile } from '../lib/protectedFiles';
import { hasPermission } from '../lib/permissions';
import { Archive, ArrowLeft, CalendarClock, CheckCircle2, ChevronDown, ChevronRight, Clock, DollarSign, LifeBuoy, MessageSquare, Paperclip, RotateCcw, Send, Star } from 'lucide-react';
import { toAppDateInputValue } from '../lib/appDateTime';

const STATUS_OPTIONS = [
  { value: 'aberto', label: 'Aberto' },
  { value: 'em_triagem', label: 'Em triagem' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'aguardando_solicitante', label: 'Aguardando solicitante' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'cancelado', label: 'Cancelado' },
];

const KANBAN_COLUMNS = [
  { value: 'aberto', label: 'Aberto' },
  { value: 'em_triagem', label: 'Triagem' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'aguardando_solicitante', label: 'Aguardando' },
  { value: 'concluido', label: 'Concluído' },
];

const PRIORITY_OPTIONS = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const AREA_OPTIONS = [
  { value: 'outro', label: 'Outro' },
  { value: 'admissao', label: 'Admissão' },
  { value: 'prontuario', label: 'Prontuário' },
  { value: 'alimentacao', label: 'Alimentação' },
  { value: 'medicacao', label: 'Medicação' },
  { value: 'laboratorio', label: 'Laboratório' },
  { value: 'necropsia', label: 'Necropsia' },
  { value: 'relatorios', label: 'Relatórios' },
  { value: 'suporte', label: 'Suporte' },
];

const CLASSIFICATION_OPTIONS = [
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'servico_adicional', label: 'Novo serviço' },
  { value: 'revisao_humana', label: 'Revisão humana' },
];

const classificationClass = {
  manutencao: 'bg-emerald-100 text-emerald-700',
  servico_adicional: 'bg-purple-100 text-purple-700',
  revisao_humana: 'bg-amber-100 text-amber-800',
};

const classificationStatusLabel = {
  pending: 'IA analisando solicitação...',
  completed: 'Análise concluída',
  fallback: 'Análise automática indisponível — aguardando revisão',
};

const unconfirmedClassificationLabel = (ticket) => {
  if (ticket.classification_status === 'pending') return 'IA analisando solicitação...';
  if (ticket.classification_status === 'fallback') return 'Análise automática indisponível — aguardando revisão';
  return 'Tipo de atendimento: Em análise';
};

const commercialStatusLabel = {
  nao_aplicavel: 'Não aplicável',
  aguardando_orcamento: 'Aguardando orçamento',
  aguardando_aprovacao: 'Aguardando aprovação',
  aprovado: 'Aprovado',
  rejeitado: 'Rejeitado',
};

const priorityClass = {
  baixa: 'bg-slate-100 text-slate-700',
  normal: 'bg-sky-100 text-sky-700',
  alta: 'bg-amber-100 text-amber-800',
  urgente: 'bg-red-100 text-red-700',
};

const statusClass = {
  aberto: 'bg-slate-100 text-slate-700',
  em_triagem: 'bg-violet-100 text-violet-700',
  em_andamento: 'bg-blue-100 text-blue-700',
  aguardando_solicitante: 'bg-amber-100 text-amber-800',
  concluido: 'bg-emerald-100 text-emerald-700',
  cancelado: 'bg-gray-100 text-gray-600',
};

const formatDateTime = (value) => {
  if (!value) return 'Sem prazo';
  return new Date(value).toLocaleString('pt-BR');
};

const isOverdue = (value, status) => {
  if (!value || ['concluido', 'cancelado'].includes(status)) return false;
  return new Date(value).getTime() < Date.now();
};

const needsRequesterApproval = (ticket) => ticket.status === 'concluido' && !ticket.archived_at;

const getRequestErrorMessage = (error, fallback) => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (status && detail) return `${fallback}: ${status} - ${detail}`;
  if (status) return `${fallback}: erro ${status}`;
  return fallback;
};

export default function SupportTicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bases, setBases] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [comment, setComment] = useState('');
  const [acceptComment, setAcceptComment] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedTimelineDates, setExpandedTimelineDates] = useState({});
  const [programmerUpdate, setProgrammerUpdate] = useState({ status: 'em_triagem', priority: 'normal', delivery_due_at: '' });
  const [classificationReview, setClassificationReview] = useState({ service_classification: 'revisao_humana', comment: '' });
  const [commercialProposal, setCommercialProposal] = useState({ commercial_scope: '', estimated_hours: '', commercial_price: '', commercial_notes: '' });
  const [approvalComment, setApprovalComment] = useState('');
  const [commercialApprovals, setCommercialApprovals] = useState([]);
  const [validationSettings, setValidationSettings] = useState(null);
  const [form, setForm] = useState({
    base_id: 'none',
    title: '',
    description: '',
    priority: 'normal',
    support_area: 'outro',
  });

  const isProgrammer = user?.role === 'programador';
  const isPaula = `${user?.name || ''} ${user?.full_name || ''} ${user?.email || ''}`.toLowerCase().includes('paula');
  const canManageSupport = hasPermission(user, 'support_tickets.manage');
  const canApproveCommercial = hasPermission(user, 'support.commercial.approve');
  const visibleTickets = showArchived
    ? tickets.filter((ticket) => ticket.archived_at || ['concluido', 'cancelado'].includes(ticket.status))
    : tickets.filter((ticket) => !ticket.archived_at);
  const approvalTickets = visibleTickets.filter(needsRequesterApproval);
  const regularTickets = visibleTickets.filter((ticket) => !needsRequesterApproval(ticket));
  const listSelectedTicket = visibleTickets.find((ticket) => ticket.id === selectedTicketId) || visibleTickets[0];
  const selectedTicket = selectedTicketDetail?.id === listSelectedTicket?.id ? selectedTicketDetail : listSelectedTicket;

  useEffect(() => {
    fetchInitialData();
  }, [showArchived]);

  useEffect(() => {
    if (visibleTickets.length > 0 && !visibleTickets.some((ticket) => ticket.id === selectedTicketId)) {
      setSelectedTicketId(visibleTickets[0].id);
    }
  }, [visibleTickets, selectedTicketId]);

  useEffect(() => {
    if (!listSelectedTicket?.id) {
      setSelectedTicketDetail(null);
      return;
    }
    fetchTicketDetail(listSelectedTicket.id);
  }, [listSelectedTicket?.id]);

  useEffect(() => {
    if (!selectedTicket?.id || selectedTicket.classification_status !== 'pending') return;
    const timer = window.setInterval(async () => {
      try {
        const response = await axios.get(`${API}/support-tickets/${selectedTicket.id}`);
        setSelectedTicketDetail(response.data);
        setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? { ...ticket, ...response.data } : ticket));
      } catch (error) {
        console.error('Erro ao atualizar triagem do suporte:', error);
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [selectedTicket?.id, selectedTicket?.classification_status]);

  useEffect(() => {
    if (!selectedTicket) return;
    const deliveryDate = selectedTicket.delivery_due_at
      ? new Date(selectedTicket.delivery_due_at).toISOString().slice(0, 16)
      : '';
    setProgrammerUpdate({
      status: selectedTicket.status || 'em_triagem',
      priority: selectedTicket.priority || 'normal',
      delivery_due_at: deliveryDate,
    });
    setClassificationReview({
      service_classification: selectedTicket.service_classification || 'revisao_humana',
      comment: '',
    });
    setCommercialProposal({
      commercial_scope: selectedTicket.commercial_scope || '',
      estimated_hours: selectedTicket.estimated_hours ? String(selectedTicket.estimated_hours) : '',
      commercial_price: selectedTicket.commercial_price_cents ? String(selectedTicket.commercial_price_cents / 100) : '',
      commercial_notes: selectedTicket.commercial_notes || '',
    });
    setApprovalComment('');
  }, [selectedTicket?.id]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const ticketsResponse = await axios.get(`${API}/support-tickets${showArchived ? '?include_archived=true' : ''}`);
      setTickets(ticketsResponse.data || []);
      if (canApproveCommercial) {
        const approvalsResponse = await axios.get(`${API}/support-tickets/commercial-approvals`);
        setCommercialApprovals(approvalsResponse.data || []);
      }
      if (isProgrammer || user?.role === 'administrador') {
        const settingsResponse = await axios.get(`${API}/support-tickets/settings/validation-exceptions`);
        setValidationSettings(settingsResponse.data || null);
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Erro ao carregar chamados'));
    } finally {
      setLoading(false);
    }

    try {
      const basesResponse = await axios.get(`${API}/bases`);
      setBases(basesResponse.data || []);
    } catch (error) {
      console.error('Error fetching bases for support:', error);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    setLoadingTicketDetail(true);
    try {
      const response = await axios.get(`${API}/support-tickets/${ticketId}`);
      setSelectedTicketDetail(response.data);
    } catch (error) {
      setSelectedTicketDetail(null);
      toast.error(getRequestErrorMessage(error, 'Erro ao carregar detalhes do chamado'));
    } finally {
      setLoadingTicketDetail(false);
    }
  };

  const groupedTickets = useMemo(() => {
    return KANBAN_COLUMNS.reduce((acc, column) => {
      acc[column.value] = tickets.filter((ticket) => ticket.status === column.value && !ticket.archived_at && !needsRequesterApproval(ticket));
      return acc;
    }, {});
  }, [tickets]);

  const timelineItems = useMemo(() => {
    return tickets
      .flatMap((ticket) => [
        { id: `${ticket.id}-created`, ticket, type: 'Abertura', date: ticket.created_at, text: ticket.title },
        ...(ticket.history || []).map((item) => ({
          id: item.id,
          ticket,
          type: item.action,
          date: item.created_at,
          text: item.details?.comment || item.details?.status?.after || item.details?.priority?.after || ticket.title,
        })),
      ])
      .filter((item) => item.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 30);
  }, [tickets]);

  const timelineGroups = useMemo(() => {
    return timelineItems.reduce((groups, item) => {
      const key = toAppDateInputValue(item.date);
      if (!groups[key]) {
        groups[key] = {
          key,
          label: new Date(item.date).toLocaleDateString('pt-BR'),
          items: [],
        };
      }
      groups[key].items.push(item);
      return groups;
    }, {});
  }, [timelineItems]);

  const orderedTimelineGroups = useMemo(() => {
    return Object.values(timelineGroups).sort((a, b) => b.key.localeCompare(a.key));
  }, [timelineGroups]);

  const uploadFiles = async (ticketId, files) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/support-tickets/${ticketId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/support-tickets`, {
        base_id: form.base_id === 'none' ? null : form.base_id,
        title: form.title,
        description: form.description,
        priority: form.priority,
        support_area: form.support_area,
      });
      if (pendingFiles.length > 0) {
        await uploadFiles(response.data.id, pendingFiles);
      }
      toast.success('Chamado aberto');
      setForm({ base_id: 'none', title: '', description: '', priority: 'normal', support_area: 'outro' });
      setPendingFiles([]);
      await fetchInitialData();
      setSelectedTicketId(response.data.id);
      setSelectedTicketDetail(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir chamado');
    } finally {
      setSaving(false);
    }
  };

  const handleProgrammerUpdate = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    try {
      const payload = {
        status: programmerUpdate.status,
        priority: programmerUpdate.priority,
        delivery_due_at: programmerUpdate.delivery_due_at ? new Date(programmerUpdate.delivery_due_at).toISOString() : null,
        comment: comment || null,
        assignee_user_id: user.id,
      };
      const response = await axios.put(`${API}/support-tickets/${selectedTicket.id}`, payload);
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      setComment('');
      toast.success('Chamado atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar chamado');
    } finally {
      setSaving(false);
    }
  };

  const handleComment = async () => {
    if (!selectedTicket || !comment.trim()) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/support-tickets/${selectedTicket.id}/comments`, { comment });
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      setComment('');
      toast.success('Comentário registrado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao comentar chamado');
    } finally {
      setSaving(false);
    }
  };

  const handlePrioritize = async (ticketId) => {
    try {
      const response = await axios.post(`${API}/support-tickets/${ticketId}/prioritize`);
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      toast.success('Chamado priorizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao priorizar chamado');
    }
  };

  const handleAcceptTicket = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    try {
      await axios.post(`${API}/support-tickets/${selectedTicket.id}/accept`, {
        comment: acceptComment || null,
      });
      toast.success('Chamado aceito e arquivado');
      setAcceptComment('');
      setSelectedTicketDetail(null);
      await fetchInitialData();
      setSelectedTicketId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aceitar chamado');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestReview = async () => {
    if (!selectedTicket) return;
    if (!reviewComment.trim()) {
      toast.error('Informe o que precisa ser revisado');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/support-tickets/${selectedTicket.id}/request-review`, {
        comment: reviewComment.trim(),
      });
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      setReviewComment('');
      setAcceptComment('');
      toast.success('Chamado reaberto para revisão');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar revisão');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAttachment = async (attachment) => {
    try {
      await openProtectedFile(attachment.file_url, attachment.filename);
    } catch (error) {
      toast.error(error.message || 'Erro ao abrir anexo');
    }
  };

  const handleClassificationReview = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/support-tickets/${selectedTicket.id}/classification-review`, classificationReview);
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      toast.success('Classificação validada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao validar classificação');
    } finally {
      setSaving(false);
    }
  };

  const handleCommercialProposal = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    try {
      const priceText = String(commercialProposal.commercial_price || '').replace(',', '.');
      const payload = {
        commercial_scope: commercialProposal.commercial_scope,
        estimated_hours: commercialProposal.estimated_hours ? Number(commercialProposal.estimated_hours) : null,
        commercial_price_cents: priceText ? Math.round(Number(priceText) * 100) : null,
        commercial_currency: 'BRL',
        commercial_notes: commercialProposal.commercial_notes || null,
      };
      const response = await axios.post(`${API}/support-tickets/${selectedTicket.id}/commercial-proposal`, payload);
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      await fetchInitialData();
      toast.success('Proposta enviada para aprovação');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar proposta');
    } finally {
      setSaving(false);
    }
  };

  const handleCommercialApproval = async (ticketId, approved) => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/support-tickets/${ticketId}/commercial-approval`, {
        approved,
        approval_comment: approvalComment || null,
      });
      setTickets((current) => current.map((ticket) => ticket.id === response.data.id ? response.data : ticket));
      setSelectedTicketDetail(response.data);
      await fetchInitialData();
      setApprovalComment('');
      toast.success(approved ? 'Serviço aprovado' : 'Serviço não aprovado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar decisão comercial');
    } finally {
      setSaving(false);
    }
  };

  const handleValidationSettingChange = async (field, active) => {
    try {
      const payload = field === 'reports'
        ? { reports_active: active }
        : { necropsy_active: active };
      const response = await axios.put(`${API}/support-tickets/settings/validation-exceptions`, payload);
      setValidationSettings(response.data);
      toast.success('Configuração atualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar configuração');
    }
  };

  const toggleTimelineDate = (key) => {
    setExpandedTimelineDates((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const renderTicketCard = (ticket) => (
    <button
      key={ticket.id}
      type="button"
      onClick={() => setSelectedTicketId(ticket.id)}
      className={`w-full rounded-md border bg-white p-3 text-left hover:border-emerald-300 ${selectedTicket?.id === ticket.id ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-slate-200'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-slate-900">{ticket.title}</p>
        <Badge className={priorityClass[ticket.priority] || priorityClass.normal}>{ticket.priority}</Badge>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {ticket.support_area && <Badge className="bg-slate-100 text-slate-700">{AREA_OPTIONS.find((item) => item.value === ticket.support_area)?.label || ticket.support_area}</Badge>}
        {ticket.classification_reviewed_at
          ? <Badge className={classificationClass[ticket.service_classification] || classificationClass.revisao_humana}>{CLASSIFICATION_OPTIONS.find((item) => item.value === ticket.service_classification)?.label || ticket.service_classification}</Badge>
          : ticket.classification_status && <Badge className="bg-slate-100 text-slate-700">{unconfirmedClassificationLabel(ticket)}</Badge>}
        {ticket.billing_exempt && <Badge className="bg-cyan-100 text-cyan-800">Em validação - sem cobrança</Badge>}
        {ticket.commercial_status && ticket.commercial_status !== 'nao_aplicavel' && <Badge className="bg-purple-100 text-purple-700">{commercialStatusLabel[ticket.commercial_status] || ticket.commercial_status}</Badge>}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        <strong>Solicitante:</strong> {ticket.requester_name || 'N/A'} {ticket.base_name ? `• ${ticket.base_name}` : ''}
      </p>
      {ticket.status === 'concluido' && !ticket.archived_at && (
        <p className="mt-1 text-xs font-medium text-emerald-700">Aguardando aceite do solicitante</p>
      )}
      <div className="mt-2 space-y-1 text-xs text-slate-600">
        <p className={isOverdue(ticket.first_response_due_at, ticket.status) && !ticket.first_response_at ? 'font-medium text-red-600' : ''}>
          1ª resposta: {ticket.first_response_at ? formatDateTime(ticket.first_response_at) : formatDateTime(ticket.first_response_due_at)}
        </p>
        <p className={isOverdue(ticket.delivery_due_at, ticket.status) ? 'font-medium text-red-600' : ''}>
          Entrega: {formatDateTime(ticket.delivery_due_at)}
        </p>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Suporte Interno</h1>
              <p className="text-sm text-slate-500">Chamados, prazos, histórico e acompanhamento</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchInitialData}>Atualizar</Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <LifeBuoy className="h-5 w-5 text-emerald-700" />
              Abrir chamado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTicket} className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <div>
                <Label>Base</Label>
                <Select value={form.base_id} onValueChange={(value) => setForm((current) => ({ ...current, base_id: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem base</SelectItem>
                    {bases.map((base) => <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(value) => setForm((current) => ({ ...current, priority: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Área</Label>
                <Select value={form.support_area} onValueChange={(value) => setForm((current) => ({ ...current, support_area: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AREA_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="lg:col-span-1">
                <Label>Título</Label>
                <Input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} required />
              </div>
              <div className="lg:col-span-4">
                <Label>Descrição</Label>
                <Textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} required rows={3} />
              </div>
              <div className="lg:col-span-3">
                <Label>Anexos</Label>
                <CameraFileInput multiple accept="image/*,video/*,.pdf,.doc,.docx,.txt" onFilesSelected={setPendingFiles} fileLabel="Selecionar anexos" cameraLabel="Tirar foto" />
                {pendingFiles.length > 0 && <p className="mt-1 text-xs text-slate-500">{pendingFiles.length} arquivo(s) selecionado(s)</p>}
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800">
                  <Send className="mr-2 h-4 w-4" />
                  {saving ? 'Salvando...' : 'Abrir chamado'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {(isProgrammer || user?.role === 'administrador') && validationSettings && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Validação sem cobrança</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 md:flex-row">
              <div className="flex flex-1 items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">Relatórios</p>
                  <p className="text-xs text-slate-500">Chamados abertos durante a validação ficam isentos.</p>
                </div>
                <Button variant={validationSettings.reports?.active ? 'default' : 'outline'} onClick={() => handleValidationSettingChange('reports', !validationSettings.reports?.active)}>
                  {validationSettings.reports?.active ? 'ATIVA' : 'ENCERRADA'}
                </Button>
              </div>
              <div className="flex flex-1 items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">Necropsia</p>
                  <p className="text-xs text-slate-500">Chamados abertos durante a validação ficam isentos.</p>
                </div>
                <Button variant={validationSettings.necropsy?.active ? 'default' : 'outline'} onClick={() => handleValidationSettingChange('necropsy', !validationSettings.necropsy?.active)}>
                  {validationSettings.necropsy?.active ? 'ATIVA' : 'ENCERRADA'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {canApproveCommercial && commercialApprovals.length > 0 && (
          <Card className="border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-purple-700" />
                Serviços aguardando sua aprovação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {commercialApprovals.map((ticket) => (
                <div key={ticket.id} className="rounded-md border bg-white p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{ticket.title}</p>
                      <p className="text-sm text-slate-600">{ticket.requester_name || 'N/A'} {ticket.base_name ? `• ${ticket.base_name}` : ''}</p>
                      <p className="mt-2 text-sm"><strong>Escopo:</strong> {ticket.commercial_scope}</p>
                      <p className="text-sm">
                        <strong>Horas:</strong> {ticket.estimated_hours || 'N/A'} • <strong>Valor:</strong> {ticket.commercial_price_cents != null ? (ticket.commercial_price_cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: ticket.commercial_currency || 'BRL' }) : 'N/A'}
                      </p>
                      {ticket.commercial_notes && <p className="text-sm text-slate-600">{ticket.commercial_notes}</p>}
                    </div>
                    <Button variant="outline" onClick={() => setSelectedTicketId(ticket.id)}>Ver chamado</Button>
                  </div>
                  <Textarea className="mt-3" placeholder="Comentário de aprovação/rejeição" value={approvalComment} onChange={(event) => setApprovalComment(event.target.value)} />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => handleCommercialApproval(ticket.id, true)}>Aprovar</Button>
                    <Button variant="outline" className="text-red-700" onClick={() => handleCommercialApproval(ticket.id, false)}>Não aprovar</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {isProgrammer && (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            {KANBAN_COLUMNS.map((column) => (
              <Card key={column.value} className="min-h-[220px]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-700">{column.label} ({groupedTickets[column.value]?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[390px] space-y-3 overflow-y-auto pr-2">
                  {(groupedTickets[column.value] || []).map(renderTicketCard)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-lg">
                <span>Solicitações</span>
                <Button variant="outline" size="sm" onClick={() => setShowArchived((current) => !current)}>
                  <Archive className="mr-2 h-4 w-4" />
                  {showArchived ? 'Ocultar finalizados' : 'Consultar finalizados'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[520px] space-y-3 overflow-y-auto pr-2">
              {loading ? (
                <p className="text-sm text-slate-500">Carregando chamados...</p>
              ) : visibleTickets.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum chamado registrado.</p>
              ) : (
                <div className="space-y-5">
                  {approvalTickets.length > 0 && (
                    <section className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div>
                          <p className="font-semibold text-emerald-900">Solicitações para aprovação</p>
                          <p className="text-xs text-emerald-800">Chamados concluídos aguardando aceite do solicitante.</p>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-800">{approvalTickets.length}</Badge>
                      </div>
                      <div className="space-y-3">
                        {approvalTickets.map(renderTicketCard)}
                      </div>
                    </section>
                  )}

                  <section className="space-y-3">
                    {approvalTickets.length > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-800">Demais solicitações</p>
                        <Badge className="bg-slate-100 text-slate-700">{regularTickets.length}</Badge>
                      </div>
                    )}
                    {regularTickets.length > 0 ? (
                      regularTickets.map(renderTicketCard)
                    ) : (
                      <p className="text-sm text-slate-500">Nenhuma outra solicitação.</p>
                    )}
                  </section>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalhes</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[760px] overflow-y-auto pr-3">
              {selectedTicket ? (
                <div className="space-y-4">
                  {loadingTicketDetail && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-500">
                      Carregando detalhes do chamado...
                    </div>
                  )}
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={statusClass[selectedTicket.status] || statusClass.aberto}>{STATUS_OPTIONS.find((item) => item.value === selectedTicket.status)?.label || selectedTicket.status}</Badge>
                      <Badge className={priorityClass[selectedTicket.priority] || priorityClass.normal}>{selectedTicket.priority}</Badge>
                      {selectedTicket.prioritized_by_paula && <Badge className="bg-pink-100 text-pink-700">Prioridade Paula</Badge>}
                    </div>
                    <h2 className="mt-2 text-lg font-semibold text-slate-900">{selectedTicket.title}</h2>
                    <p className="text-sm text-slate-500">
                      <strong>Solicitante:</strong> {selectedTicket.requester_name || 'N/A'} • {formatDateTime(selectedTicket.created_at)}
                    </p>
                    <p className="text-sm text-slate-500">
                      Base: {selectedTicket.base_name || 'Sem base'} • Responsável: {selectedTicket.assignee_name || 'Não definido'}
                    </p>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{selectedTicket.description || 'Carregando descrição...'}</p>
                  </div>

                  <div className="rounded-md border bg-white p-3">
                    <div className="mb-2 flex flex-wrap gap-2">
                      {selectedTicket.support_area && <Badge className="bg-slate-100 text-slate-700">{AREA_OPTIONS.find((item) => item.value === selectedTicket.support_area)?.label || selectedTicket.support_area}</Badge>}
                      {selectedTicket.classification_reviewed_at
                        ? <Badge className={classificationClass[selectedTicket.service_classification] || classificationClass.revisao_humana}>Classificação confirmada pela equipe: {CLASSIFICATION_OPTIONS.find((item) => item.value === selectedTicket.service_classification)?.label || selectedTicket.service_classification}</Badge>
                        : selectedTicket.classification_status && <Badge className="bg-slate-100 text-slate-700">{unconfirmedClassificationLabel(selectedTicket)}</Badge>}
                      {selectedTicket.billing_exempt && <Badge className="bg-cyan-100 text-cyan-800">EM VALIDAÇÃO - SEM COBRANÇA</Badge>}
                      {selectedTicket.commercial_status && selectedTicket.commercial_status !== 'nao_aplicavel' && <Badge className="bg-purple-100 text-purple-700">{commercialStatusLabel[selectedTicket.commercial_status] || selectedTicket.commercial_status}</Badge>}
                    </div>
                    {isProgrammer && (selectedTicket.classification_status || selectedTicket.classification_summary) && (
                      <div className="text-sm text-slate-700">
                        <p className="font-semibold text-slate-800">Análise automática</p>
                        <p><strong>Status:</strong> {classificationStatusLabel[selectedTicket.classification_status] || selectedTicket.classification_status || 'N/A'}</p>
                        {selectedTicket.ai_service_classification && (
                          <p><strong>Sugestão:</strong> {CLASSIFICATION_OPTIONS.find((item) => item.value === selectedTicket.ai_service_classification)?.label || selectedTicket.ai_service_classification}</p>
                        )}
                        <p><strong>Confiança:</strong> {selectedTicket.classification_confidence != null ? `${Math.round(selectedTicket.classification_confidence * 100)}%` : 'N/A'}</p>
                        <p><strong>Resumo:</strong> {selectedTicket.classification_summary || 'N/A'}</p>
                        <p><strong>Motivo:</strong> {selectedTicket.classification_reason || 'N/A'}</p>
                        {isProgrammer && (
                          <p><strong>Fonte:</strong> {selectedTicket.classification_source || 'N/A'} {selectedTicket.classification_model ? `• ${selectedTicket.classification_model}` : ''}</p>
                        )}
                        {selectedTicket.classification_indicators?.length > 0 && <p><strong>Indicadores:</strong> {selectedTicket.classification_indicators.join(', ')}</p>}
                      </div>
                    )}
                    {selectedTicket.billing_exempt && (
                      <p className="mt-2 rounded-md bg-cyan-50 p-2 text-sm text-cyan-900">
                        Solicitação dentro de etapa ainda em validação - sem cobrança. Motivo: {selectedTicket.billing_exemption_reason || selectedTicket.commercial_exception}.
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                    <div className="rounded-md bg-slate-50 p-3">
                      <p className="text-slate-500">Resposta inicial</p>
                      <p className={isOverdue(selectedTicket.first_response_due_at, selectedTicket.status) && !selectedTicket.first_response_at ? 'font-semibold text-red-600' : 'font-medium'}>
                        {selectedTicket.first_response_at ? formatDateTime(selectedTicket.first_response_at) : formatDateTime(selectedTicket.first_response_due_at)}
                      </p>
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <p className="text-slate-500">Prazo de entrega</p>
                      <p className={isOverdue(selectedTicket.delivery_due_at, selectedTicket.status) ? 'font-semibold text-red-600' : 'font-medium'}>{formatDateTime(selectedTicket.delivery_due_at)}</p>
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <p className="text-slate-500">Conclusão</p>
                      <p className="font-medium">{formatDateTime(selectedTicket.completed_at)}</p>
                    </div>
                  </div>

                  {isProgrammer && (
                    <div className="space-y-3 rounded-md border p-3">
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                        <p className="mb-2 font-semibold text-slate-800">Classificação confirmada pela equipe</p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[220px_1fr_auto]">
                          <Select value={classificationReview.service_classification} onValueChange={(value) => setClassificationReview((current) => ({ ...current, service_classification: value }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CLASSIFICATION_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input placeholder="Comentário interno" value={classificationReview.comment} onChange={(event) => setClassificationReview((current) => ({ ...current, comment: event.target.value }))} />
                          <Button type="button" variant="outline" onClick={handleClassificationReview} disabled={saving}>Confirmar</Button>
                        </div>
                      </div>

                      {selectedTicket.service_classification === 'servico_adicional' && !selectedTicket.billing_exempt && (
                        <div className="rounded-md border border-purple-200 bg-purple-50 p-3">
                          <p className="mb-2 font-semibold text-purple-900">Orçamento de serviço adicional</p>
                          <div className="grid grid-cols-1 gap-3">
                            <Textarea placeholder="Escopo do serviço" value={commercialProposal.commercial_scope} onChange={(event) => setCommercialProposal((current) => ({ ...current, commercial_scope: event.target.value }))} />
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <Input type="number" step="0.25" placeholder="Horas estimadas" value={commercialProposal.estimated_hours} onChange={(event) => setCommercialProposal((current) => ({ ...current, estimated_hours: event.target.value }))} />
                              <Input type="number" step="0.01" placeholder="Valor em R$" value={commercialProposal.commercial_price} onChange={(event) => setCommercialProposal((current) => ({ ...current, commercial_price: event.target.value }))} />
                            </div>
                            <Textarea placeholder="Observações comerciais" value={commercialProposal.commercial_notes} onChange={(event) => setCommercialProposal((current) => ({ ...current, commercial_notes: event.target.value }))} />
                            <Button type="button" className="bg-purple-700 hover:bg-purple-800" onClick={handleCommercialProposal} disabled={saving || !commercialProposal.commercial_scope.trim()}>
                              Enviar para aprovação
                            </Button>
                          </div>
                        </div>
                      )}

                      {selectedTicket.status === 'concluido' && !selectedTicket.archived_at && (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                          Chamado concluído. Aguardando o solicitante testar e aceitar para arquivar.
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div>
                          <Label>Status</Label>
                          <Select value={programmerUpdate.status} onValueChange={(value) => setProgrammerUpdate((current) => ({ ...current, status: value }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Prioridade</Label>
                          <Select value={programmerUpdate.priority} onValueChange={(value) => setProgrammerUpdate((current) => ({ ...current, priority: value }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{PRIORITY_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Prazo de entrega</Label>
                          <Input type="datetime-local" value={programmerUpdate.delivery_due_at} onChange={(event) => setProgrammerUpdate((current) => ({ ...current, delivery_due_at: event.target.value }))} />
                        </div>
                      </div>
                      <Textarea placeholder="Registre o que foi feito ou a resposta inicial" value={comment} onChange={(event) => setComment(event.target.value)} />
                      <Button onClick={handleProgrammerUpdate} disabled={saving} className="bg-slate-900 hover:bg-slate-800">Salvar atendimento</Button>
                    </div>
                  )}

                  {(!isProgrammer || (selectedTicket.status === 'concluido' && !selectedTicket.archived_at)) && (
                    <div className="space-y-2">
                      {selectedTicket.status === 'concluido' && !selectedTicket.archived_at && (
                        <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-4 shadow-sm">
                          <p className="text-base font-semibold text-emerald-900">Aprovar conclusão do chamado</p>
                          <p className="mt-1 text-sm text-emerald-800">
                            O programador marcou este chamado como concluído. Teste o que foi implantado e escolha se está aprovado ou se precisa voltar para revisão.
                          </p>
                          <Textarea
                            className="mt-2"
                            placeholder="Comentário de aceite (opcional)"
                            value={acceptComment}
                            onChange={(event) => setAcceptComment(event.target.value)}
                          />
                          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                            <Button onClick={handleAcceptTicket} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
                              <CheckCircle2 className="mr-2 h-4 w-4" />
                              Aceitar e arquivar
                            </Button>
                          </div>
                          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                            <p className="text-sm font-semibold text-amber-900">Precisa de revisão?</p>
                            <p className="mt-1 text-xs text-amber-800">
                              Informe o que ficou faltando. O card volta para Em andamento no kanban e o prazo é renovado sem apagar o histórico.
                            </p>
                            <Textarea
                              className="mt-2"
                              placeholder="Descreva o que precisa ser revisado"
                              value={reviewComment}
                              onChange={(event) => setReviewComment(event.target.value)}
                            />
                            <Button onClick={handleRequestReview} disabled={saving || !reviewComment.trim()} variant="outline" className="mt-3 border-amber-300 bg-white text-amber-800 hover:bg-amber-100">
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Solicitar revisão
                            </Button>
                          </div>
                        </div>
                      )}
                      <Textarea placeholder="Adicionar comentário" value={comment} onChange={(event) => setComment(event.target.value)} />
                      <div className="flex gap-2">
                        <Button onClick={handleComment} disabled={saving || !comment.trim()} variant="outline">
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Comentar
                        </Button>
                        {isPaula && (
                          <Button onClick={() => handlePrioritize(selectedTicket.id)} variant="outline" className="text-pink-700">
                            <Star className="mr-2 h-4 w-4" />
                            Priorizar
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedTicket.attachments?.length > 0 && (
                    <div>
                      <p className="mb-2 font-medium text-slate-800">Anexos</p>
                      <div className="space-y-2">
                        {selectedTicket.attachments.map((attachment) => (
                          <button key={attachment.id} type="button" onClick={() => handleOpenAttachment(attachment)} className="flex w-full items-center justify-between rounded border p-2 text-left text-sm hover:bg-slate-50">
                            <span className="flex items-center gap-2"><Paperclip className="h-4 w-4" />{attachment.filename}</span>
                            <span className="text-xs text-slate-500">{formatDateTime(attachment.uploaded_at)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="mb-2 font-medium text-slate-800">Histórico</p>
                    <div className="space-y-2">
                      {[...(selectedTicket.comments || []), ...(selectedTicket.history || [])]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .slice(0, 12)
                        .map((item) => (
                          <div key={item.id} className="rounded bg-slate-50 p-2 text-xs">
                            <p className="font-medium text-slate-700">{item.user_name} • {formatDateTime(item.created_at)}</p>
                            <p className="text-slate-600">{item.comment || item.details?.comment || item.action}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Selecione um chamado.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {isProgrammer && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-slate-700" />
                Linha do tempo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {orderedTimelineGroups.map((group) => (
                  <div key={group.key} className="rounded-md border bg-white">
                    <button
                      type="button"
                      onClick={() => toggleTimelineDate(group.key)}
                      className="flex w-full items-center justify-between p-3 text-left"
                    >
                      <span className="flex items-center gap-2 font-medium text-slate-800">
                        {expandedTimelineDates[group.key] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {group.label}
                      </span>
                      <Badge className="bg-slate-100 text-slate-700">{group.items.length}</Badge>
                    </button>
                    {expandedTimelineDates[group.key] && (
                      <div className="max-h-[360px] space-y-3 overflow-y-auto border-t p-3">
                        {group.items
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map((item) => (
                            <div key={item.id} className="flex gap-3 border-l-2 border-slate-200 pl-4">
                              <div className="mt-1">
                                {item.ticket.status === 'concluido' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <CalendarClock className="h-4 w-4 text-slate-500" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{item.ticket.title}</p>
                                <p className="text-xs text-slate-500">{new Date(item.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} • {item.type}</p>
                                <p className="text-xs text-slate-600">{item.text}</p>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
